// Shared shaping and validation for a lender's saved Config.
//
// This lives on its own because the types matter and the UI has three separate
// entry points into them: the add-lender dialog, the configuration drawer, and
// the save path every edit funnels through. LenderFee.Fee is a float64 server
// side, so a fee that reaches the API as a string fails to unmarshal and takes
// that lender's pricing down with it — see lenderConfig.test.js.

/** Tolerant numeric read. Accepts numbers, "1,500", "" and junk; never NaN. */
export const parseAmount = (v) => {
    const n = parseFloat(String(v === null || v === undefined ? "" : v).replace(/,/g, ""))
    return isNaN(n) ? 0 : n
}

/** Optional text field: blank becomes null, which is what the API expects. */
export const textOrNull = (v) => {
    const s = String(v === null || v === undefined ? "" : v).replace(/,/g, "").trim()
    return s === "" ? null : s
}

/**
 * States already claimed by another fee row on the same lender. A state can
 * only carry one origination fee; two rows naming it produce conflicting fees.
 * Works on anything with a `states` array — saved lenderfees or draft overrides.
 */
export const statesUsedByOtherRows = (rows, skipIndex) => {
    const used = new Set()
    ;(rows || []).forEach((row, i) => {
        if (i === skipIndex) return
        ;(row.states || []).forEach((s) => used.add(s))
    })
    return used
}

/** Indexes of saved fee rows whose amount is missing or zero. */
export const invalidFeeRows = (fees) =>
    (fees || []).map((f, i) => (parseAmount(f.fee) <= 0 ? i : -1)).filter((i) => i >= 0)

/**
 * Same check against the add-lender dialog's draft shape, which keeps the
 * default fee separate from its overrides. Returns marker strings the dialog
 * uses to highlight fields. An override with no states yet is not held against
 * the user — it is dropped on save rather than persisted.
 */
export const feeErrorsFor = (draft) => {
    const errors = []
    if (parseAmount(draft.fee) <= 0) errors.push("default")
    ;(draft.overrides || []).forEach((ov, i) => {
        if (ov.states && ov.states.length > 0 && parseAmount(ov.amount) <= 0) {
            errors.push("override-" + i)
        }
    })
    return errors
}

/**
 * Largest closing cost limit, either sign, in percent of the loan amount.
 * Points, fees and prepaid interest rarely reach 10%; anything past it was a
 * dollar amount typed into a percent field (1195 meant $1,195).
 */
export const CLOSING_COST_LIMIT_PERCENT = 10

/**
 * What is wrong with a lender's closing cost limits, or null when nothing is.
 *
 * The calculator keeps a rate only when min <= closing cost / loan amount * 100
 * <= max and drops the whole product when no rate is left, so a window nothing
 * can land in hides the lender from every search without saying why. Seen in
 * production on 2026-11-01: min = max = 0, min = max = 3, min 6 above max 1.5,
 * and dollar amounts such as min 1195 or 3500.
 */
export const closingCostWindowError = (minRaw, maxRaw) => {
    const read = (raw) => {
        const s = textOrNull(raw)
        return s === null ? null : Number(s)
    }
    const min = read(minRaw)
    const max = read(maxRaw)
    const limit = CLOSING_COST_LIMIT_PERCENT
    if ([min, max].some((v) => v !== null && !isFinite(v))) {
        return "Closing cost limits must be numbers, in percent of the loan amount."
    }
    if ([min, max].some((v) => v !== null && Math.abs(v) > limit)) {
        return "Closing cost limits are a percent of the loan amount, between -" + limit +
            "% and " + limit + "%. Enter 3 for 3%, not a dollar amount."
    }
    if (min !== null && max !== null && min >= max) {
        return "Min closing cost must be lower than max closing cost, or no rate can fall between them."
    }
    return null
}

/**
 * Force every fee in a Config to a number, in place.
 *
 * Called on the way out in postLenders, which is the one point all save paths
 * funnel through: no edit route can persist a string, and a row that already
 * holds one is repaired the next time its lender is saved.
 */
export const normalizeLenderFees = (config) => {
    if (config && Array.isArray(config.lenderfees)) {
        config.lenderfees.forEach((f) => {
            f.fee = parseAmount(f.fee)
        })
    }
    return config
}

/**
 * Map the add-lender dialog's draft onto a saved Config.
 *
 * `previous` is the Config of a lender that was connected before, so its rules
 * and promotions survive being re-added. The default fee is written first with
 * no states (meaning everywhere), then one row per state override.
 */
export const buildLenderConfig = (form, previous) => {
    const lenderfees = [{
        fee: parseAmount(form.fee),
        feeFormat: form.feeType || "flat",
        states: [],
        mortgageTypes: [],
    }]
    ;(form.overrides || []).forEach((ov) => {
        if (!ov.states || ov.states.length === 0) return
        lenderfees.push({
            fee: parseAmount(ov.amount),
            feeFormat: ov.type || "flat",
            states: ov.states,
            mortgageTypes: [],
        })
    })
    return {
        ...(previous || {}),
        compensation: textOrNull(form.comp) === null ? "0" : String(form.comp).trim(),
        compensationFlatFee: parseAmount(form.flat),
        minimumCompensation: textOrNull(form.min),
        maximumCompensation: textOrNull(form.max),
        minimumClosingCostPercentage: textOrNull(form.minCC),
        maximumClosingCostPercentage: textOrNull(form.maxCC),
        correspondentLender: form.rel,
        closeover: (previous && previous.closeover) || null,
        lenderfees,
    }
}

/**
 * A min or max compensation field, in dollars: null when blank (no limit), NaN
 * when it is not a plain number. The calculator reads these with Go's ",string"
 * tag, so anything but digits and one dot ("1,500", "$0") fails to decode and
 * drops the whole lender from pricing.
 */
export const readDollarLimit = (raw) => {
    const s = textOrNull(raw)
    if (s === null) return null
    return /^(\d+\.?\d*|\.\d+)$/.test(s) ? Number(s) : NaN
}

/** What is wrong with a lender's min/max compensation, or null when nothing is. */
export const compensationLimitError = (minRaw, maxRaw) => {
    const min = readDollarLimit(minRaw)
    const max = readDollarLimit(maxRaw)
    if ([min, max].some((v) => v !== null && !isFinite(v))) {
        return "Min and max compensation are dollar amounts, such as 2500. Leave blank for no limit."
    }
    if (min !== null && max !== null && min > max) {
        return "Min compensation can't be higher than max compensation."
    }
    return null
}

/**
 * What a lender pays on a loan of `loanAmount`, the way the calculator works it
 * out (calculation.getCompensationPoint): percent of the loan plus the flat fee,
 * then capped at the max, then raised to the min. `by` says which limit decided
 * the final amount, or null when neither did.
 */
export const compensationPreview = (config, loanAmount) => {
    const percent = parseAmount(config && config.compensation)
    const flat = parseAmount(config && config.compensationFlatFee)
    const min = readDollarLimit(config && config.minimumCompensation)
    const max = readDollarLimit(config && config.maximumCompensation)
    const uncapped = (loanAmount * percent) / 100 + flat
    let amount = uncapped
    let by = null
    if (max !== null && isFinite(max) && amount > max) {
        amount = max
        by = "max"
    }
    if (min !== null && isFinite(min) && amount < min) {
        amount = min
        by = "min"
    }
    return { percent, flat, min, max, uncapped, amount, by }
}

/**
 * True when the max compensation is $0 while the percentage or flat fee would
 * pay something: the lender then pays nothing on any loan. Legal, and some
 * companies mean it, but in production (2026-11-04) most of the 27 such rows
 * were "0.00" typed into the old form by people who meant "no limit".
 */
export const maxCompensationZeroesPay = (config) => {
    const p = compensationPreview(config || {}, 400000)
    return p.max === 0 && p.uncapped > 0
}

export const MAX_COMPENSATION_ZERO_CONFIRM =
    "Max compensation $0 means you earn nothing from this lender on any loan. " +
    "To have no limit, leave Max compensation blank. Save with $0 anyway?"
